import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing camera stream lifecycle
 * Handles camera initialization, stream management, and cleanup
 *
 * @param {Object} options Configuration options
 * @param {boolean} options.autoStart - If true, starts camera when mounted
 * @param {Function} options.onError - Callback when camera access fails
 * @returns {Object} Camera stream functions and state
 */
export function useCameraStream(options = {}) {
  const { autoStart = false, onError } = options;

  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  /**
   * Starts the camera stream
   * @param {Object} constraints - MediaStream constraints (optional)
   */
  const startCamera = useCallback(async (constraints = null) => {
    try {
      setError(null);

      const defaultConstraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(
        constraints || defaultConstraints
      );

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setIsRunning(true);
    } catch (err) {
      console.error('[Camera] Failed to start stream:', err);
      const errorMessage = err.name === 'NotAllowedError'
        ? 'Camera access denied. Please allow camera permissions.'
        : err.name === 'NotFoundError'
        ? 'No camera found on this device.'
        : `Camera error: ${err.message}`;

      setError(errorMessage);
      setIsRunning(false);

      if (onError) {
        onError(err);
      }
    }
  }, [onError]);

  /**
   * Stops the camera stream and releases resources
   */
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsRunning(false);
  }, []);

  /**
   * Captures a frame from the video stream as a data URL
   * @param {HTMLCanvasElement} canvas - Canvas element to draw to
   * @param {string} format - Image format ('image/png' or 'image/jpeg')
   * @param {number} quality - JPEG quality (0-1), only for image/jpeg
   * @returns {string|null} Base64 data URL or null if capture fails
   */
  const captureFrame = useCallback((canvas, format = 'image/png', quality = 1.0) => {
    if (!videoRef.current || !isRunning) {
      console.warn('[Camera] Cannot capture: camera not running');
      return null;
    }

    const video = videoRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('[Camera] Failed to get canvas context');
      return null;
    }

    // Set canvas size to match video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to data URL
    const dataUrl = canvas.toDataURL(format, quality);

    return dataUrl;
  }, [isRunning]);

  /**
   * Gets current video dimensions
   * @returns {Object} Width and height of video stream
   */
  const getVideoDimensions = useCallback(() => {
    if (!videoRef.current) {
      return { width: 0, height: 0 };
    }

    return {
      width: videoRef.current.videoWidth,
      height: videoRef.current.videoHeight
    };
  }, []);

  // Auto-start camera if requested
  useEffect(() => {
    if (autoStart) {
      startCamera();
    }

    // Cleanup on unmount
    return () => {
      stopCamera();
    };
  }, [autoStart]); // Note: startCamera/stopCamera excluded to avoid re-runs

  return {
    videoRef,
    isRunning,
    error,
    startCamera,
    stopCamera,
    captureFrame,
    getVideoDimensions,
    stream: streamRef.current
  };
}
